import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, UserCog, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * SurgeryCollaboratorsSection — sección plugable en el formulario de cirugía.
 *
 * Permite añadir uno o varios cirujanos colaboradores con sus honorarios
 * calculados desde el tarifario. El importe puede sobrescribirse puntualmente.
 *
 * Props:
 *   value: array de colaboradores actuales
 *          [{surgeon_id, surgeon_name, procedure_id, procedure_name, default_fee, override_fee, final_fee, notes}]
 *   onChange: (newArray) => void
 *   selectedProcedures: array de procedimientos de la cirugía (para poder sugerir cuáles)
 *   procedures: catálogo completo de procedimientos (para nombres)
 *   totalPrice: precio total de la cirugía (para calcular neto)
 */
export default function SurgeryCollaboratorsSection({
  value = [], onChange, selectedProcedures = [], procedures = [], totalPrice = 0,
  // Regla de negocio: la ayudantía se descuenta del monto del cirujano
  // principal EXCEPTO cuando la paga la aseguradora directamente
  // (ADESLAS/ASISA). Pasa ambos props para reflejarlo en el "Neto".
  assistantFee = 0, assistantFeeType = null,
}) {
  const [surgeons, setSurgeons] = useState([]);
  const [feesBySurgeon, setFeesBySurgeon] = useState({});  // { surgeon_id: [fees...] }
  const [loading, setLoading] = useState(false);

  // Formulario de "añadir colaborador"
  const [pickSurgeonId, setPickSurgeonId] = useState('');
  const [pickProcedureId, setPickProcedureId] = useState('');
  const [pickOverride, setPickOverride] = useState('');

  const loadSurgeons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/collaborating-surgeons');
      setSurgeons(res.data || []);
    } catch {
      // silent — el módulo puede no estar configurado aún
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSurgeons(); }, [loadSurgeons]);

  // Cargar tarifario del cirujano al seleccionarlo
  useEffect(() => {
    if (!pickSurgeonId) return;
    if (feesBySurgeon[pickSurgeonId]) return;
    (async () => {
      try {
        const res = await api.get(`/collaborating-surgeons/${pickSurgeonId}/fees`);
        setFeesBySurgeon((prev) => ({ ...prev, [pickSurgeonId]: res.data || [] }));
      } catch {
        // ignore
      }
    })();
  }, [pickSurgeonId, feesBySurgeon]);

  const availableProcedures = selectedProcedures.length > 0
    ? selectedProcedures
        .map((sp) => procedures.find((p) => p.id === sp.procedure_id))
        .filter(Boolean)
    : procedures;

  const currentFees = feesBySurgeon[pickSurgeonId] || [];
  const feeForProcedure = pickProcedureId
    ? currentFees.find((f) => f.procedure_id === pickProcedureId)
    : null;
  const defaultFee = feeForProcedure?.fee_amount ?? null;

  const canAdd = pickSurgeonId && pickProcedureId;

  const handleAdd = () => {
    if (!canAdd) {
      toast.error('Selecciona cirujano y procedimiento');
      return;
    }
    const surgeon = surgeons.find((s) => s.id === pickSurgeonId);
    const proc = procedures.find((p) => p.id === pickProcedureId);
    const overrideFee = pickOverride !== '' ? parseFloat(pickOverride) : null;
    const finalFee = overrideFee != null && !Number.isNaN(overrideFee)
      ? overrideFee
      : (defaultFee || 0);

    // No duplicar la misma combinación surgeon+procedure
    const dup = value.some((c) => c.surgeon_id === pickSurgeonId && c.procedure_id === pickProcedureId);
    if (dup) {
      toast.error('Este colaborador ya está asignado a ese procedimiento');
      return;
    }

    const newCollab = {
      surgeon_id: surgeon?.id,
      surgeon_name: surgeon?.name || '',
      procedure_id: proc?.id || null,
      procedure_name: proc?.name || '',
      default_fee: defaultFee,
      override_fee: overrideFee,
      final_fee: finalFee,
      notes: null,
    };
    onChange([...value, newCollab]);
    // Reset picker
    setPickProcedureId('');
    setPickOverride('');
  };

  const handleRemove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleOverrideChange = (idx, newVal) => {
    const arr = [...value];
    const override = newVal === '' ? null : parseFloat(newVal);
    arr[idx] = {
      ...arr[idx],
      override_fee: override != null && !Number.isNaN(override) ? override : null,
      final_fee: override != null && !Number.isNaN(override)
        ? override
        : (arr[idx].default_fee || 0),
    };
    onChange(arr);
  };

  const totalCollaboratorFees = value.reduce((sum, c) => sum + (c.final_fee || 0), 0);
  // Solo se descuenta la ayudantía cuando NO la paga directamente la
  // aseguradora (percentage / manual). Con `insurance_direct` el pago sale
  // fuera del bolsillo del cirujano principal, así que no se resta.
  const assistantDeduction = (assistantFeeType && assistantFeeType !== 'insurance_direct')
    ? Number(assistantFee || 0)
    : 0;
  const netForMainSurgeon = totalPrice - totalCollaboratorFees - assistantDeduction;

  return (
    <div className="space-y-3 border rounded-xl p-3 bg-purple-50/40 border-purple-200/70">
      <div className="flex items-center gap-2">
        <UserCog size={18} className="text-purple-700" />
        <Label className="font-semibold text-purple-900">
          Cirujanos Colaboradores <span className="text-xs font-normal text-gray-500">(opcional)</span>
        </Label>
      </div>

      {surgeons.length === 0 && !loading && (
        <div className="text-xs text-gray-600 flex items-start gap-2 bg-white/70 p-2 rounded">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>
            Aún no hay colaboradores configurados. Configúralos en <b>Ajustes → Cirujanos Colaboradores</b>.
          </span>
        </div>
      )}

      {surgeons.length > 0 && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4">
            <Label className="text-xs">Colaborador</Label>
            <Select value={pickSurgeonId} onValueChange={setPickSurgeonId}>
              <SelectTrigger data-testid="collab-surgeon-select">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {surgeons.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-4">
            <Label className="text-xs">Procedimiento</Label>
            <Select
              value={pickProcedureId}
              onValueChange={setPickProcedureId}
              disabled={!pickSurgeonId}
            >
              <SelectTrigger data-testid="collab-procedure-select">
                <SelectValue placeholder={pickSurgeonId ? 'Selecciona...' : 'Selecciona cirujano primero'} />
              </SelectTrigger>
              <SelectContent>
                {availableProcedures.map((p) => {
                  const fee = currentFees.find((f) => f.procedure_id === p.id);
                  return (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {fee ? `— ${fee.fee_amount.toFixed(0)}€` : '(sin tarifa)'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3">
            <Label className="text-xs">
              Importe {defaultFee != null && (
                <span className="text-gray-400 font-normal">(tarifa: {defaultFee.toFixed(0)}€)</span>
              )}
            </Label>
            <div className="relative">
              <Input
                type="number" step="0.01" min="0"
                value={pickOverride}
                onChange={(e) => setPickOverride(e.target.value)}
                placeholder={defaultFee != null ? String(defaultFee) : '0'}
                className="pr-6"
                data-testid="collab-override-input"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
            </div>
          </div>
          <div className="col-span-1">
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              disabled={!canAdd}
              className="bg-purple-700 hover:bg-purple-800 w-full"
              data-testid="collab-add-btn"
            >
              <Plus size={14} />
            </Button>
          </div>
        </div>
      )}

      {value.length > 0 && (
        <div className="space-y-1.5 pt-2 border-t border-purple-200">
          {value.map((c, idx) => (
            <div
              key={`${c.surgeon_id}-${c.procedure_id}-${idx}`}
              className="grid grid-cols-12 gap-2 items-center bg-white rounded-lg border p-2"
              data-testid={`collab-row-${idx}`}
            >
              <div className="col-span-4 text-sm font-medium text-purple-900">
                {c.surgeon_name}
              </div>
              <div className="col-span-4 text-sm text-gray-600 truncate" title={c.procedure_name}>
                {c.procedure_name}
              </div>
              <div className="col-span-3 flex items-center gap-1">
                <Input
                  type="number" step="0.01" min="0"
                  value={c.override_fee != null ? c.override_fee : (c.default_fee ?? c.final_fee ?? 0)}
                  onChange={(e) => handleOverrideChange(idx, e.target.value)}
                  className="w-full text-right text-sm h-8"
                  data-testid={`collab-row-fee-${idx}`}
                />
                <span className="text-xs text-gray-400">€</span>
              </div>
              <div className="col-span-1 flex justify-end">
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => handleRemove(idx)}
                  className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                  data-testid={`collab-remove-${idx}`}
                >
                  <Trash2 size={12} />
                </Button>
              </div>
              {c.default_fee != null && c.override_fee != null && c.override_fee !== c.default_fee && (
                <div className="col-span-12 text-[10px] text-amber-700 pl-1">
                  ⚠ Importe sobrescrito (tarifa habitual: {c.default_fee.toFixed(2)}€)
                </div>
              )}
            </div>
          ))}

          <div className="flex flex-wrap justify-between items-center pt-2 gap-2 text-sm">
            <Badge variant="outline" className="bg-purple-100 text-purple-900 border-purple-300">
              Total colaboradores: {totalCollaboratorFees.toFixed(2)}€
            </Badge>
            {assistantDeduction > 0 && (
              <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300">
                Ayudantía descontada: {assistantDeduction.toFixed(2)}€
              </Badge>
            )}
            {totalPrice > 0 && (
              <Badge variant="outline" className="bg-emerald-100 text-emerald-900 border-emerald-300">
                Neto cirujano principal: {netForMainSurgeon.toFixed(2)}€
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
